import React, { useState, useEffect } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { collegeAccessControlService } from '../../lib/college-access-control-service';
import { Shield, User, UserCheck, Settings, AlertCircle } from 'lucide-react';

export default function CollegeAccessControl() {
  const { familyUsers, family } = useFamilyContext();
  const [moduleAccess, setModuleAccess] = useState({});
  const [isLoadingAccess, setIsLoadingAccess] = useState(true);
  const [accessError, setAccessError] = useState(null);

  // Get all children from the family
  const allChildren = familyUsers?.filter(user => user.type === 'child') || [];

  // Load access settings from database on component mount
  useEffect(() => {
    const loadAccessSettings = async () => {
      if (!family?.id) return;
      
      setIsLoadingAccess(true);
      try {
        console.log('🔍 Loading access settings for family:', family.id);
        const accessData = await collegeAccessControlService.getModuleAccess(family.id, 'college-journey');
        console.log('✅ Access data loaded:', accessData);
        
        // Convert array to object for easier lookup
        const accessMap = {};
        accessData.forEach(access => {
          accessMap[access.user_id] = access.has_access;
        });
        
        setModuleAccess(accessMap);
      } catch (error) {
        console.error('❌ Error loading access settings:', error);
        setAccessError('Failed to load access settings');
      } finally {
        setIsLoadingAccess(false);
      }
    };

    loadAccessSettings();
  }, [family?.id]);

  const updateModuleAccess = async (childId, hasAccess) => {
    try {
      setAccessError(null);
      console.log('🔄 Updating access for child:', childId, 'hasAccess:', hasAccess);
      
      await collegeAccessControlService.updateModuleAccess(
        family.id,
        childId,
        'college-journey',
        hasAccess
      );
      
      // Update local state
      setModuleAccess(prev => ({
        ...prev,
        [childId]: hasAccess
      }));
      
      console.log('✅ Access updated successfully');
    } catch (error) {
      console.error('❌ Error updating access:', error);
      setAccessError('Failed to update access settings. Please try again.');
    }
  };

  return (
    <div className="bg-neutral-50 rounded-2xl shadow-sm border border-stone-100 mb-8">
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-stone-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-teal-600" />
            College Journey Access Control
          </h2>
          <p className="text-stone-600 mt-1">
            Manage which children can access the college journey hub and assessment tools
          </p>
          {accessError && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-red-800">
                <AlertCircle className="w-4 h-4" />
                <p className="text-sm font-medium">{accessError}</p>
              </div>
            </div>
          )}
        </div>
        
        {isLoadingAccess ? (
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center gap-2 text-stone-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-stone-600"></div>
              <span>Loading access settings...</span>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {allChildren.map((child) => (
              <div key={child.id} className="flex items-center justify-between p-4 border border-stone-200 rounded-xl bg-white">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-stone-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-stone-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-stone-900">
                      {child.displayName || child.name}
                    </h3>
                    <p className="text-sm text-stone-600">
                      {child.email || `${child.type || 'Student'} account`}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-medium ${
                    moduleAccess[child.id] !== false ? 'text-emerald-600' : 'text-red-600'
                  }`}>
                    {moduleAccess[child.id] !== false ? 'Has Access' : 'No Access'}
                  </span>
                  <button
                    onClick={() => updateModuleAccess(child.id, moduleAccess[child.id] === false)}
                    disabled={isLoadingAccess}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      moduleAccess[child.id] !== false
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                    }`}
                  >
                    {moduleAccess[child.id] !== false ? 'Revoke Access' : 'Grant Access'}
                  </button>
                </div>
              </div>
            ))}
            
            {allChildren.length === 0 && (
              <div className="text-center py-8 text-stone-500">
                <UserCheck className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No children found in your family</p>
                <p className="text-sm mt-1">Add children to your family to manage access</p>
              </div>
            )}
          </div>
        )}
        
        <div className="mt-6 bg-azure-50 border border-azure-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Settings className="w-5 h-5 text-azure-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-azure-900">
              <p className="font-semibold mb-1">Access Control Notes</p>
              <ul className="space-y-1 text-azure-800">
                <li>• Children with access can view and take the college journey assessment</li>
                <li>• Revoking access will hide the module from their navigation</li>
                <li>• You can grant or revoke access at any time</li>
                <li>• All children have access by default when first added</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}